/**
 * 阿里云 OSS / CDN 图片处理（x-oss-process）
 *
 * 用法：
 *   buildOssImageUrl(url)                          // 默认转 webp
 *   buildOssImageUrl(url, { strategy: "avatar" })  // 预设策略
 *   buildOssImageUrl(url, { w: 300, q: 80 })       // 自定义宽/质量
 *   buildOssImageUrl(url, { strategy: "resize", w: 214, h: 214, mode: "pad" })
 *
 * 扩展：registerOssImageStrategy("banner", opts => [...])
 */

const OSS_HOSTS = [
	"media.seeronnet.com",
	"sandbox-media.seeronnet.com",
	"cdn.seeronnet.com",
	"img-fe.seersecret.com",
	"fe-gw.seeronnet.com",
];

/** @type {Record<string, (opts: Record<string, any>) => string[]>} */
const strategies = {
	/** 仅转格式（默认） */
	webp: ({ q = 80 } = {}) => ["format,webp", `quality,q_${q}`],

	/** 头像 */
	avatar: ({ w = 96, h = 96, q = 80 } = {}) => [
		`resize,m_fill,w_${w},h_${h}`,
		"format,webp",
		`quality,q_${q}`,
	],

	/** 封面/卡片图：按宽等比缩放 */
	cover: ({ w = 800, q = 80 } = {}) => [`resize,m_lfit,w_${w}`, "format,webp", `quality,q_${q}`],

	/** 定宽高填充（商城缩略图常用） */
	resize: ({ w, h, mode = "pad", q = 80 } = {}) => {
		const parts = ["resize", `m_${mode}`];
		if (w) parts.push(`w_${w}`);
		if (h) parts.push(`h_${h}`);
		return [parts.join(","), "format,webp", `quality,q_${q}`];
	},

	/** 自定义：直接传 ops 数组或 process 字符串 */
	custom: ({ ops, process } = {}) => {
		if (Array.isArray(ops) && ops.length) return ops;
		if (typeof process === "string" && process) {
			return process
				.replace(/^image\//, "")
				.split("/")
				.filter(Boolean);
		}
		return ["format,webp"];
	},
};

export function isOssImageHost(src) {
	if (!src || typeof src !== "string") return false;
	try {
		const host = new URL(src).hostname;
		return OSS_HOSTS.some(item => host === item || host.endsWith(`.${item}`));
	} catch {
		return false;
	}
}

export function registerOssImageStrategy(name, builder) {
	if (!name || typeof builder !== "function") return;
	strategies[name] = builder;
}

export function getOssImageStrategies() {
	return Object.keys(strategies);
}

/**
 * @param {string} src
 * @param {{
 *   strategy?: string,
 *   w?: number|string,
 *   h?: number|string,
 *   q?: number|string,
 *   mode?: string,
 *   format?: string,
 *   ops?: string[],
 *   process?: string,
 *   force?: boolean,
 * }} [options]
 */
export function buildOssImageUrl(src, options = {}) {
	if (!src || typeof src !== "string") return src || "";
	if (!/^https?:\/\//.test(src)) return src;
	if (!isOssImageHost(src)) return src;

	// 已带处理参数：默认不重复拼接（force 可覆盖）
	if (src.includes("x-oss-process=") && !options.force) return src;

	const strategyName = options.strategy || inferStrategy(options);
	const builder = strategies[strategyName] || strategies.webp;
	let ops = builder(options);

	// 允许额外指定 format（覆盖策略里的 format）
	if (options.format) {
		ops = ops.filter(op => !String(op).startsWith("format,"));
		ops.push(`format,${options.format}`);
	}

	// 无 strategy、但传了 w/h：补 resize
	if (!options.strategy && (options.w || options.h) && strategyName === "webp") {
		const resize = ["resize", "m_lfit"];
		if (options.w) resize.push(`w_${options.w}`);
		if (options.h) resize.push(`h_${options.h}`);
		ops = [resize.join(","), ...ops];
	}

	const process = `image/${ops.join("/")}`;
	const cleaned = src.replace(/([?&])x-oss-process=[^&]*/g, "$1").replace(/[?&]$/, "");
	const joiner = cleaned.includes("?") ? "&" : "?";
	return `${cleaned}${joiner}x-oss-process=${process}`;
}

function inferStrategy(options = {}) {
	if (options.ops || options.process) return "custom";
	if (options.mode || (options.w && options.h)) return "resize";
	return "webp";
}
